import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from 'components/card';
import Select from 'react-select';
import { fieldEmojis, frenchAreas } from 'variables/industries';
import { useIndustries } from 'services/industryService';
import { CONFIG } from 'variables/config';

const PurchaseOrder = () => {
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const navigate = useNavigate();
    
    // Build industryDataFields from API data (back-office configuration)
    const buildIndustryDataFields = () => {
        const mapping: { [key: string]: any[] } = {};
        
        industriesData.forEach(industry => {
            const fields: any[] = [];
            
            // Use ONLY mandatoryFields from back-office configuration
            if (industry.mandatoryFields && industry.mandatoryFields.length > 0) {
                industry.mandatoryFields.forEach((field: string) => {
                    fields.push({
                        label: field,
                        icon: fieldEmojis[field] || '📋'
                    });
                });
            }
            
            // Use lowercase key for easier matching
            const key = industry.name.toLowerCase().replace(/\s+/g, '-');
            mapping[key] = fields;
        });
        
        return mapping;
    };

    // Build industryProducts from API data
    const buildIndustryProducts = () => {
        const mapping: { [key: string]: string[] } = {};
        
        industriesData.forEach(industry => {
            const products = industry.subIndustries;
            if (products && Array.isArray(products)) {
                const productNames = products.map((p: any) => p.name);
                const key = industry.name.toLowerCase().replace(/\s+/g, '-');
                mapping[key] = productNames;
            }
        });
        
        return mapping;
    };

    const industryDataFields = buildIndustryDataFields();
    const industryProducts = buildIndustryProducts();

    const [price, setPrice] = useState('15.00');
    const [leadCount, setLeadCount] = useState('50');
    const [industry, setIndustry] = useState('rénovation');
    const [product, setProduct] = useState('panneaux-solaires');
    const [departments, setDepartments] = useState<string[]>([]);
    const [showProductField, setShowProductField] = useState(true);
    const [dataFields, setDataFields] = useState(
        industryDataFields['rénovation'] || []
    );
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // Update data fields when industry changes
        if (industry && industryDataFields[industry]) {
            setDataFields(industryDataFields[industry]);
        }
        
        // Update product options when industry changes
        if (industry && industryProducts[industry]) {
            setShowProductField(true);
            // Set first product as default
            const firstProduct = industryProducts[industry][0]
                .toLowerCase()
                .replace(/\s+/g, '-');
            setProduct(firstProduct);
        }
    }, [industry]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const leadCountNum = parseInt(leadCount);
        const priceNum = parseFloat(price);

        if (leadCountNum < 1) {
            alert('❌ Veuillez indiquer un nombre de leads souhaité');
            return;
        }

        if (priceNum <= 0) {
            alert('❌ Le prix doit être supérieur à 0€');
            return;
        }

        if (!industry) {
            alert('❌ Veuillez sélectionner une industrie');
            return;
        }

        if (!product) {
            alert('❌ Veuillez sélectionner un produit');
            return;
        }

        if (!departments || departments.length === 0) {
            alert('❌ Veuillez sélectionner au moins un département');
            return;
        }

        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            
            // First, get current user ID
            const userResponse = await axios.get(
                `${CONFIG.apiUrl}/user/me`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const userId = userResponse.data._id;

            // Create the purchase order
            const response = await axios.post(
                `${CONFIG.apiUrl}/purchase-order`,
                {
                    price: priceNum,
                    leadCount: leadCountNum,
                    industry,
                    product,
                    departments,
                    dataFields,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const purchaseOrderId = response.data.purchaseOrder._id;

            // Redirect to Stripe checkout
            window.location.href = `${CONFIG.apiUrl}/stripe/checkout-purchase-order/${purchaseOrderId}/${userId}`;
        } catch (error: any) {
            console.error('Error creating purchase order:', error);
            alert(
                `❌ Erreur lors de la création de l'ordre d'achat:\n${error.response?.data?.message || error.message}`
            );
            setSubmitting(false);
        }
    };

    if (industriesLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-xl font-bold text-navy-700 dark:text-white">
                    Chargement des industries...
                </div>
            </div>
        );
    }

    return (
        <div className="h-full">
            <div className="w-full p-0 overflow-hidden">
                {/* Header Section */}
                <div className="rounded-[20px] border border-gray-200/50 bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50 p-8 shadow-lg dark:border-white/10 dark:from-navy-800 dark:to-navy-900">
                    {/* Title and subtitle */}
                    <div className="mb-6">
                        <h1 className="mb-2 text-[32px] font-bold leading-tight text-gray-900 dark:text-white">
                            Ordre d'achat
                        </h1>
                        <p className="text-base text-gray-600 dark:text-gray-400">
                            La bourse du lead. Vous fixez vos conditions, plus vous payez plus vous êtes prioritaire. Idéal pour des leads locaux sans engagement.
                        </p>
                    </div>

                    {/* Two columns layout */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Comment ça marche */}
                        <div className="rounded-[16px] border border-gray-200/50 bg-white/60 p-6 backdrop-blur-sm dark:border-white/10 dark:bg-navy-800/60">
                            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-lg dark:bg-blue-900/30">
                                    ℹ️
                                </span>
                                Comment ça marche ?
                            </h3>

                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 font-bold text-leadrs">✓</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Fixez votre prix :</strong> Définissez le prix exact que vous souhaitez payer par lead
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 font-bold text-leadrs">✓</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Matching automatique :</strong> La plateforme se charge de faire le matching entre les leads des vendeurs et vos conditions
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 font-bold text-leadrs">✓</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Système de priorité :</strong> Plus votre prix est élevé, plus vous êtes prioritaire sur votre zone géographique
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Les avantages */}
                        <div className="rounded-[16px] border border-gray-200/50 bg-white/60 p-6 backdrop-blur-sm dark:border-white/10 dark:bg-navy-800/60">
                            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-lg dark:bg-green-900/30">
                                    ✨
                                </span>
                                Les avantages
                            </h3>

                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <span className="text-lg">💰</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Prix maîtrisé :</strong> Vous choisissez combien vous voulez payer par lead. Plus votre prix est compétitif, plus vite vous recevez vos leads
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-lg">⚡</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Instantané :</strong> Dès qu'un lead correspond et que vous êtes prioritaire, il vous est livré automatiquement
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-lg">⏱️</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Livraison progressive :</strong> Livraison entre 1 à 4 semaines en fonction des disponibilités sur le marché
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-lg">🎯</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Leads 100% exclusifs :</strong> Un seul acheteur par lead, toujours
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-lg">🔓</span>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>Aucun engagement :</strong> Pas de volume minimum ni de zone géographique minimum à couvrir
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Body */}
                <div className="flex justify-center py-6">
                    <div className="w-full rounded-[20px] border border-gray-200/50 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-navy-800">
                        {/* Form */}
                        <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {/* Prix maximum */}
                        <div>
                            <label className="mb-2 flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                                <span className="text-lg">💶</span>
                                Prix maximum par lead
                                <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 bg-white/0 px-4 py-3 pr-12 text-sm outline-none transition duration-200 focus:border-leadrs dark:border-white/10 dark:text-white"
                                    placeholder="15.00"
                                    required
                                />
                                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-gray-600">
                                    €
                                </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-600">
                                Le prix que vous êtes prêt à payer pour chaque
                                lead
                            </p>
                        </div>

                        {/* Nombre de leads */}
                        <div>
                            <label className="mb-2 flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                                <span className="text-lg">👥</span>
                                Nombre de leads souhaités
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={leadCount}
                                onChange={(e) => setLeadCount(e.target.value)}
                                className="w-full rounded-xl border border-gray-200 bg-white/0 px-4 py-3 text-sm outline-none transition duration-200 focus:border-leadrs dark:border-white/10 dark:text-white"
                                placeholder="50"
                                required
                            />
                            <p className="mt-2 text-xs text-gray-600">
                                Nombre estimé de leads que vous souhaitez
                                recevoir (Pas de minimum de volume)
                            </p>
                        </div>

                        {/* Industrie */}
                        <div>
                            <label className="mb-2 flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                                <span className="text-lg">🏢</span>
                                Industrie
                                <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                className="w-full rounded-xl border border-gray-200 bg-white/0 px-4 py-3 text-sm outline-none transition duration-200 focus:border-leadrs dark:border-white/10 dark:text-white"
                                required
                            >
                                <option value="">
                                    Sélectionner une industrie
                                </option>
                                {industriesData.map((industryItem) => {
                                    const key = industryItem.name.toLowerCase().replace(/\s+/g, '-');
                                    return (
                                        <option key={industryItem._id} value={key}>
                                            {industryItem.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Produit */}
                        {showProductField && (
                            <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                                    <span className="text-lg">📦</span>
                                    Produit
                                    <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={product}
                                    onChange={(e) => setProduct(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 bg-white/0 px-4 py-3 text-sm outline-none transition duration-200 focus:border-leadrs dark:border-white/10 dark:text-white"
                                    required
                                >
                                    <option value="">
                                        Sélectionner un produit
                                    </option>
                                    {industry &&
                                        industryProducts[industry] &&
                                        industryProducts[industry].map(
                                            (prod) => (
                                                <option
                                                    key={prod}
                                                    value={prod
                                                        .toLowerCase()
                                                        .replace(/\s+/g, '-')}
                                                >
                                                    {prod}
                                                </option>
                                            )
                                        )}
                                </select>
                            </div>
                        )}
                        </div>

                        {/* Data Fields Info Box */}
                        {dataFields.length > 0 && (
                            <div className="mt-8 mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-navy-700">
                                <div className="mb-2 flex items-center gap-3">
                                    <span className="text-xl">📋</span>
                                    <h3 className="text-base font-bold text-navy-700 dark:text-white">
                                        Données incluses dans chaque lead
                                    </h3>
                                </div>
                                <p className="mb-4 text-xs italic text-gray-600 dark:text-gray-400">
                                    Ces champs de données sont fixes pour cette
                                    industrie et ne peuvent pas être modifiés :
                                </p>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                                    {dataFields.map((field, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                                        >
                                            <span>{field.icon}</span>
                                            <span>{field.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Départements */}
                        <div className="mb-8">
                            <label className="mb-2 flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                                <span className="text-lg">📍</span>
                                Départements ciblés
                                <span className="text-red-500">*</span>
                            </label>
                            <div className="w-full rounded-xl text-sm text-gray-600 outline-none">
                                <Select
                                    value={departments.map((region: string) => ({
                                        value: region === 'Toute la France' ? 'france' : region,
                                        label: region,
                                    }))}
                                    isMulti
                                    onChange={(items) => {
                                        const regions: string[] = [];
                                        items.map((item) =>
                                            regions.push(
                                                item.value === 'france'
                                                    ? 'Toute la France'
                                                    : item.value
                                            )
                                        );
                                        setDepartments(regions);
                                    }}
                                    name="departments"
                                    options={frenchAreas.map((item) => ({
                                        value: item === '🇫🇷 Toute la France' ? 'france' : item,
                                        label: item,
                                    }))}
                                    className="w-full"
                                    classNamePrefix="select"
                                    placeholder="Sélectionner des départements..."
                                />
                            </div>
                            <p className="mt-2 text-xs text-gray-600">
                                Ciblage local précis par département, pas de minimum de département.
                            </p>
                        </div>

                        {/* Form Actions */}
                        <div className="mt-8 flex gap-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="flex-1 rounded-xl border border-gray-200 bg-transparent px-6 py-3 text-sm font-medium text-gray-700 transition duration-200 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-leadrs px-6 py-3 text-sm font-medium text-white transition duration-200 hover:bg-brand-deep active:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span>💳</span>
                                {submitting ? 'Création en cours...' : `Payer ${(parseFloat(price || '0') * parseInt(leadCount || '0')).toFixed(2)}€ maintenant`}
                            </button>
                        </div>
                    </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrder;
